/** @format */

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";

interface HostedInvoiceView {
    businessName: string;
    businessEmail?: string;
    customerName?: string;
    invoiceNumber?: string;
    issuedAt?: string;
    dueDate?: string;
    totalAmount: number;
    balance: number;
    currency: string;
    isPaid: boolean;
    isVoid: boolean;
    qboPayUrl: string | null;
}

function fmtCurrency(amount: number, currency = "USD"): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
        }).format(amount);
    } catch {
        return `$${amount.toFixed(2)}`;
    }
}

function fmtDate(iso?: string): string {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return iso;
    }
}

const HostedInvoicePay: React.FC = () => {
    const { payToken } = useParams<{ payToken: string }>();
    const [view, setView] = useState<HostedInvoiceView | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [clicking, setClicking] = useState(false);

    useEffect(() => {
        if (!payToken) {
            setError("This payment link is not valid.");
            setLoading(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(
                    `${API_BASE_URL}/pay/invoice/${encodeURIComponent(payToken)}`,
                );
                if (cancelled) return;
                setView(res.data?.data || null);
            } catch (e: any) {
                if (cancelled) return;
                const status = e?.response?.status;
                if (status === 404) {
                    setError("We couldn't find that invoice. The link may have expired.");
                } else {
                    setError(
                        e?.response?.data?.message ||
                        "Something went wrong loading the invoice. Please try again in a moment.",
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [payToken]);

    const handlePay = async () => {
        if (!view?.qboPayUrl || !payToken) return;
        setClicking(true);
        try {
            await axios.post(
                `${API_BASE_URL}/pay/invoice/${encodeURIComponent(payToken)}/event`,
                { event: "pay_clicked" },
            );
        } catch {
            /* non-fatal — still send them to QBO */
        }
        window.location.href = view.qboPayUrl;
    };

    return (
        <div
            style={{
                minHeight: "100vh",
                background: "#f5f7fb",
                fontFamily: "'Montserrat', sans-serif",
                padding: "32px 16px",
            }}
        >
            <div
                style={{
                    maxWidth: 560,
                    margin: "0 auto",
                    background: "#fff",
                    borderRadius: 16,
                    boxShadow: "0 4px 24px rgba(15, 23, 42, 0.08)",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        padding: "24px 28px",
                        borderBottom: "1px solid #f1f5f9",
                        background:
                            "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                        color: "#fff",
                    }}
                >
                    <div style={{ fontSize: 13, opacity: 0.9, letterSpacing: 0.4 }}>
                        SECURE PAYMENT
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
                        {loading ? "Loading invoice…" : view?.businessName || "Pay Invoice"}
                    </div>
                </div>

                <div style={{ padding: "28px" }}>
                    {loading && (
                        <div style={{ color: "#475569", fontSize: 14 }}>
                            Fetching your invoice details…
                        </div>
                    )}

                    {error && !loading && (
                        <div
                            style={{
                                background: "#fef2f2",
                                border: "1px solid #fecaca",
                                color: "#991b1b",
                                padding: "14px 16px",
                                borderRadius: 10,
                                fontSize: 14,
                            }}
                        >
                            {error}
                        </div>
                    )}

                    {!loading && !error && view && (
                        <>
                            {view.isPaid && (
                                <div
                                    style={{
                                        background: "#f0fdf4",
                                        border: "1px solid #bbf7d0",
                                        color: "#166534",
                                        padding: "12px 14px",
                                        borderRadius: 10,
                                        fontSize: 14,
                                        marginBottom: 16,
                                    }}
                                >
                                    This invoice has been paid in full. Thank you!
                                </div>
                            )}
                            {view.isVoid && (
                                <div
                                    style={{
                                        background: "#fef9c3",
                                        border: "1px solid #fde68a",
                                        color: "#854d0e",
                                        padding: "12px 14px",
                                        borderRadius: 10,
                                        fontSize: 14,
                                        marginBottom: 16,
                                    }}
                                >
                                    This invoice has been voided. Please contact{" "}
                                    {view.businessName} if you have questions.
                                </div>
                            )}

                            <div style={{ marginBottom: 24 }}>
                                <div style={{ fontSize: 13, color: "#64748b" }}>
                                    Amount due
                                </div>
                                <div
                                    style={{
                                        fontSize: 38,
                                        fontWeight: 700,
                                        color: "#0f172a",
                                        marginTop: 2,
                                    }}
                                >
                                    {fmtCurrency(view.balance, view.currency)}
                                </div>
                                {view.totalAmount !== view.balance && (
                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: "#64748b",
                                            marginTop: 4,
                                        }}
                                    >
                                        Original total:{" "}
                                        {fmtCurrency(view.totalAmount, view.currency)}
                                    </div>
                                )}
                            </div>

                            <div
                                style={{
                                    background: "#f8fafc",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: 10,
                                    padding: "14px 16px",
                                    marginBottom: 24,
                                    fontSize: 14,
                                    color: "#334155",
                                }}
                            >
                                <DetailRow label="Bill to" value={view.customerName || "—"} />
                                <DetailRow
                                    label="Invoice #"
                                    value={view.invoiceNumber || "—"}
                                />
                                <DetailRow label="Issued" value={fmtDate(view.issuedAt)} />
                                <DetailRow label="Due" value={fmtDate(view.dueDate)} last />
                            </div>

                            {!view.isPaid && !view.isVoid && view.qboPayUrl && (
                                <>
                                    <button
                                        onClick={handlePay}
                                        disabled={clicking}
                                        style={{
                                            width: "100%",
                                            background:
                                                "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                                            color: "#fff",
                                            border: "none",
                                            borderRadius: 10,
                                            padding: "14px 16px",
                                            fontSize: 16,
                                            fontWeight: 600,
                                            cursor: clicking ? "wait" : "pointer",
                                            opacity: clicking ? 0.7 : 1,
                                            fontFamily: "inherit",
                                        }}
                                    >
                                        {clicking
                                            ? "Opening secure payment…"
                                            : `Pay ${fmtCurrency(view.balance, view.currency)} online`}
                                    </button>
                                    <div
                                        style={{
                                            marginTop: 14,
                                            fontSize: 13,
                                            color: "#64748b",
                                            lineHeight: 1.5,
                                            textAlign: "center",
                                        }}
                                    >
                                        You can pay the full balance or any partial amount
                                        on the next page. Even a partial payment helps keep
                                        your account in good standing.
                                    </div>
                                </>
                            )}

                            {!view.isPaid && !view.isVoid && !view.qboPayUrl && (
                                <div
                                    style={{
                                        background: "#fef9c3",
                                        border: "1px solid #fde68a",
                                        color: "#854d0e",
                                        padding: "14px 16px",
                                        borderRadius: 10,
                                        fontSize: 14,
                                    }}
                                >
                                    Online payment isn't set up for this invoice yet.
                                    Please contact{" "}
                                    <strong>{view.businessName}</strong>
                                    {view.businessEmail ? (
                                        <>
                                            {" "}at{" "}
                                            <a
                                                href={`mailto:${view.businessEmail}`}
                                                style={{ color: "#854d0e" }}
                                            >
                                                {view.businessEmail}
                                            </a>
                                        </>
                                    ) : null}{" "}
                                    to arrange payment.
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div
                    style={{
                        padding: "14px 28px",
                        borderTop: "1px solid #f1f5f9",
                        fontSize: 12,
                        color: "#94a3b8",
                        textAlign: "center",
                    }}
                >
                    Secured by FundsyFi · Payments processed by QuickBooks
                </div>
            </div>
        </div>
    );
};

const DetailRow: React.FC<{ label: string; value: string; last?: boolean }> = ({
    label,
    value,
    last,
}) => (
    <div
        style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "6px 0",
            borderBottom: last ? "none" : "1px solid #e2e8f0",
        }}
    >
        <span style={{ color: "#64748b" }}>{label}</span>
        <span style={{ color: "#0f172a", fontWeight: 500 }}>{value}</span>
    </div>
);

export default HostedInvoicePay;
